import { useState } from 'react';
import {
  useCategories, useBrands, useUnits, useTaxProfiles,
  useCreateCategory, useCreateBrand, useCreateUnit, useCreateTaxProfile,
  useDeleteCategory, useDeleteBrand, useDeleteUnit, useDeleteTaxProfile, useDeletePriceList,
  usePriceLists, useCreatePriceList, usePriceListItems, useSetPriceListItem, useRemovePriceListItem,
} from '../hooks/useCatalogApi';
import { useProductSearch } from '@/features/pos/hooks/usePosApi';
import { Trash2 } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Modal } from '@/shared/components/ui/Modal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { PlusCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatINR } from '@/shared/utils/currency';

const TABS = [{ id: 'categories', label: 'Categories' }, { id: 'brands', label: 'Brands' }, { id: 'units', label: 'Units' }, { id: 'tax', label: 'Tax Profiles' }, { id: 'price-lists', label: 'Price Lists' }];

export function MasterDataPage() {
  const [tab, setTab] = useState('categories');

  return (
    <div className="p-6 space-y-4">
      <div><h1 className="text-2xl font-semibold tracking-tight">Catalog Master Data</h1><p className="text-sm text-muted-foreground mt-1">Categories, brands, units, tax profiles and price lists</p></div>
      <div className="flex gap-2 border-b">
        {TABS.map(t => (<button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>{t.label}</button>))}
      </div>
      {tab === 'categories' && <CategoriesTab />}
      {tab === 'brands' && <BrandsTab />}
      {tab === 'units' && <UnitsTab />}
      {tab === 'tax' && <TaxProfilesTab />}
      {tab === 'price-lists' && <PriceListsTab />}
    </div>
  );
}

function CategoriesTab() {
  const { data: categories, isLoading } = useCategories();
  const createMut = useCreateCategory();
  const deleteMut = useDeleteCategory();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');

  const handleCreate = async () => { if (!name.trim()) return; await createMut.mutateAsync({ name, parentId: parentId || null }); setIsOpen(false); setName(''); toast.success('Category created'); };
  const handleDelete = async (id: string, catName: string) => {
    if (!window.confirm(`Delete category "${catName}"?`)) return;
    try { await deleteMut.mutateAsync(id); toast.success(`Category "${catName}" deleted.`); }
    catch (err: any) { toast.error(err?.response?.data?.error || 'Could not delete category.'); }
  };
  if (isLoading) return <Skeleton className="h-[200px]" />;

  return (
    <Card className="shadow-xs">
      <CardHeader title="Categories" action={<Button size="sm" onClick={() => setIsOpen(true)}><PlusCircle className="h-4 w-4 mr-1" /> Add</Button>} />
      <CardBody className="p-0"><div className="overflow-auto max-h-[400px]"><table className="w-full text-sm"><thead className="text-xs uppercase bg-muted/50 sticky top-0"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Parent</th><th className="px-4 py-3 text-right">Actions</th></tr></thead><tbody className="divide-y">{(categories?.length ?? 0) === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">No categories yet. Add one to group your products.</td></tr>}{categories?.map(c => (<tr key={c._id} className="hover:bg-muted/20"><td className="px-4 py-3 font-medium">{c.name}</td><td className="px-4 py-3 text-muted-foreground">{c.parentId?.name || '—'}</td><td className="px-4 py-3 text-right"><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDelete(c._id, c.name)}><Trash2 className="h-3.5 w-3.5" /></Button></td></tr>))}</tbody></table></div></CardBody>
      <Modal open={isOpen} onClose={() => setIsOpen(false)} title="New Category" size="sm" footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setIsOpen(false)}>Cancel</Button><Button onClick={handleCreate}>Create</Button></div>}>
        <div className="space-y-3 py-4"><Input name="name" value={name} onChange={e => setName(e.target.value)} placeholder="Category name" /><Select name="parent" value={parentId} onChange={e => setParentId(e.target.value)} options={[{ value: '', label: '— Top Level —' }, ...(categories || []).map(c => ({ value: c._id, label: c.name }))]} /></div>
      </Modal>
    </Card>
  );
}

function BrandsTab() {
  const { data: brands, isLoading } = useBrands();
  const createMut = useCreateBrand();
  const deleteMut = useDeleteBrand();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');

  const handleCreate = async () => { if (!name.trim()) return; await createMut.mutateAsync({ name }); setIsOpen(false); setName(''); toast.success('Brand created'); };
  const handleDelete = async (id: string, bName: string) => {
    if (!window.confirm(`Delete brand "${bName}"?`)) return;
    try { await deleteMut.mutateAsync(id); toast.success(`Brand "${bName}" deleted.`); }
    catch (err: any) { toast.error(err?.response?.data?.error || 'Could not delete brand.'); }
  };
  if (isLoading) return <Skeleton className="h-[200px]" />;

  return (
    <Card className="shadow-xs">
      <CardHeader title="Brands" action={<Button size="sm" onClick={() => setIsOpen(true)}><PlusCircle className="h-4 w-4 mr-1" /> Add</Button>} />
      <CardBody className="p-0"><div className="overflow-auto max-h-[400px]"><table className="w-full text-sm"><thead className="text-xs uppercase bg-muted/50 sticky top-0"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3 text-right">Actions</th></tr></thead><tbody className="divide-y">{(brands?.length ?? 0) === 0 && <tr><td colSpan={2} className="px-4 py-8 text-center text-muted-foreground">No brands yet.</td></tr>}{brands?.map(b => (<tr key={b._id} className="hover:bg-muted/20"><td className="px-4 py-3 font-medium">{b.name}</td><td className="px-4 py-3 text-right"><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDelete(b._id, b.name)}><Trash2 className="h-3.5 w-3.5" /></Button></td></tr>))}</tbody></table></div></CardBody>
      <Modal open={isOpen} onClose={() => setIsOpen(false)} title="New Brand" size="sm" footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setIsOpen(false)}>Cancel</Button><Button onClick={handleCreate}>Create</Button></div>}><div className="py-4"><Input name="name" value={name} onChange={e => setName(e.target.value)} placeholder="Brand name" /></div></Modal>
    </Card>
  );
}

function UnitsTab() {
  const { data: units, isLoading } = useUnits();
  const createMut = useCreateUnit();
  const deleteMut = useDeleteUnit();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');

  const handleCreate = async () => { if (!name.trim() || !shortName.trim()) return; await createMut.mutateAsync({ name, shortName }); setIsOpen(false); setName(''); setShortName(''); toast.success('Unit created'); };
  const handleDelete = async (id: string, uName: string) => {
    if (!window.confirm(`Delete unit "${uName}"?`)) return;
    try { await deleteMut.mutateAsync(id); toast.success(`Unit "${uName}" deleted.`); }
    catch (err: any) { toast.error(err?.response?.data?.error || 'Could not delete unit.'); }
  };
  if (isLoading) return <Skeleton className="h-[200px]" />;

  return (
    <Card className="shadow-xs">
      <CardHeader title="Units" action={<Button size="sm" onClick={() => setIsOpen(true)}><PlusCircle className="h-4 w-4 mr-1" /> Add</Button>} />
      <CardBody className="p-0"><div className="overflow-auto max-h-[400px]"><table className="w-full text-sm"><thead className="text-xs uppercase bg-muted/50 sticky top-0"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Short</th><th className="px-4 py-3 text-right">Actions</th></tr></thead><tbody className="divide-y">{units?.map(u => (<tr key={u._id} className="hover:bg-muted/20"><td className="px-4 py-3 font-medium">{u.name}</td><td className="px-4 py-3">{u.shortName}</td><td className="px-4 py-3 text-right"><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDelete(u._id, u.name)}><Trash2 className="h-3.5 w-3.5" /></Button></td></tr>))}</tbody></table></div></CardBody>
      <Modal open={isOpen} onClose={() => setIsOpen(false)} title="New Unit" size="sm" footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setIsOpen(false)}>Cancel</Button><Button onClick={handleCreate}>Create</Button></div>}><div className="space-y-3 py-4"><Input name="name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Pieces" /><Input name="short" value={shortName} onChange={e => setShortName(e.target.value)} placeholder="e.g. Pcs" /></div></Modal>
    </Card>
  );
}

/**
 * Price lists — named rate cards (wholesale, retail, contract customers). Each
 * list holds per-product rates with an optional minimum quantity, so a customer
 * or channel can be billed at an agreed price instead of the standard rate.
 */
function PriceListsTab() {
  const { data: lists, isLoading } = usePriceLists();
  const createList = useCreatePriceList();
  const deleteList = useDeletePriceList();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!name.trim()) return;
    await createList.mutateAsync({ name: name.trim(), description: description.trim() });
    setIsOpen(false); setName(''); setDescription('');
    toast.success('Price list created');
  };

  const handleDelete = async (id: string, plName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete price list "${plName}"?`)) return;
    try {
      await deleteList.mutateAsync(id);
      if (selectedId === id) setSelectedId(null);
      toast.success(`Price list "${plName}" deleted.`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not delete price list.');
    }
  };

  if (isLoading) return <Skeleton className="h-[200px]" />;

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[340px_1fr]">
      <Card className="shadow-xs">
        <CardHeader title="Price Lists" action={<Button size="sm" onClick={() => setIsOpen(true)}><PlusCircle className="h-4 w-4 mr-1" /> Add</Button>} />
        <CardBody className="p-0">
          <div className="overflow-auto max-h-[420px] divide-y">
            {(lists?.length ?? 0) === 0 && (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                No price lists yet. Create one to bill selected customers at agreed rates.
              </p>
            )}
            {lists?.map(l => (
              <div
                key={l._id}
                onClick={() => setSelectedId(l._id)}
                className={`flex w-full items-center justify-between px-4 py-3 text-left transition cursor-pointer ${selectedId === l._id ? 'bg-primary/5' : 'hover:bg-muted/20'}`}
              >
                <div className="flex flex-col">
                  <span className="font-medium">{l.name}</span>
                  <span className="text-xs text-muted-foreground">{l.description || 'No description'}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                  onClick={(e) => handleDelete(l._id, l.name, e)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </CardBody>
        <Modal open={isOpen} onClose={() => setIsOpen(false)} title="New Price List" size="sm" footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setIsOpen(false)}>Cancel</Button><Button onClick={handleCreate}>Create</Button></div>}>
          <div className="space-y-3 py-4">
            <Input name="pl-name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Wholesale rates" />
            <Input name="pl-desc" value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)" />
          </div>
        </Modal>
      </Card>

      {selectedId
        ? <PriceListItems listId={selectedId} listName={lists?.find(l => l._id === selectedId)?.name ?? 'Price list'} />
        : (
          <Card className="shadow-xs">
            <CardBody className="p-8 text-center text-sm text-muted-foreground">
              Select a price list to manage its product rates.
            </CardBody>
          </Card>
        )}
    </div>
  );
}

function PriceListItems({ listId, listName }: { listId: string; listName: string }) {
  const { data: items, isLoading } = usePriceListItems(listId);
  const setItem = useSetPriceListItem();
  const removeItem = useRemovePriceListItem();
  const [price, setPrice] = useState('');
  const [minQuantity, setMinQuantity] = useState('1');
  const [product, setProduct] = useState<{ _id: string; name: string } | null>(null);

  const add = async () => {
    if (!product || !Number(price)) { toast.error('Select a product and enter a rate.'); return; }
    await setItem.mutateAsync({ listId, productId: product._id, price: Number(price), minQuantity: Number(minQuantity) || 1 });
    setProduct(null); setPrice(''); setMinQuantity('1');
    toast.success('Rate saved');
  };

  return (
    <Card className="shadow-xs">
      <CardHeader title={`Rates — ${listName}`} subtitle="Per-product prices that override the standard selling rate" />
      <CardBody className="space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[260px] flex-1">
            <label className="mb-1.5 block text-sm font-medium">Product</label>
            <ProductSearchDropdown
              selected={product}
              onSelect={(p) => { setProduct(p); if (!price && p.sellingPrice) setPrice(String(p.sellingPrice)); }}
              onClear={() => setProduct(null)}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium">Rate</label>
            <input type="number" min="0" step="0.01" className="w-28 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" value={price} onChange={e => setPrice(e.target.value)} placeholder="₹" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium">Min qty</label>
            <input type="number" min="1" className="w-20 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" value={minQuantity} onChange={e => setMinQuantity(e.target.value)} />
          </div>
          <Button onClick={add} disabled={setItem.isPending}>Save rate</Button>
        </div>

        {isLoading ? <Skeleton className="h-[200px]" /> : (
          <div className="overflow-auto max-h-[380px] rounded-md border">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase bg-muted/50 sticky top-0">
                <tr><th className="px-4 py-3 text-left">Product</th><th className="px-4 py-3 text-right">Rate</th><th className="px-4 py-3 text-right">Min qty</th><th className="px-4 py-3" /></tr>
              </thead>
              <tbody className="divide-y">
                {(items?.length ?? 0) === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No rates in this list yet.</td></tr>
                )}
                {items?.map(it => {
                  const p = typeof it.productId === 'string' ? { name: it.productId, sku: '' } : it.productId;
                  return (
                    <tr key={it._id} className="hover:bg-muted/20">
                      <td className="px-4 py-3 font-medium">{p.name}<div className="text-xs text-muted-foreground">{p.sku}</div></td>
                      <td className="px-4 py-3 text-right font-mono">₹{it.price.toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-right">{it.minQuantity}</td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" aria-label={`Remove ${p.name}`} onClick={() => removeItem.mutate({ listId, itemId: it._id })}>
                          <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** Dropdown with embedded search box for selecting a product */
function ProductSearchDropdown({ selected, onSelect, onClear }: {
  selected: { _id: string; name: string } | null;
  onSelect: (p: { _id: string; name: string; sellingPrice?: number }) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { data: results = [], isLoading: searching } = useProductSearch(search);

  return (
    <div className="relative">
      {/* Trigger button */}
      {selected ? (
        <div className="flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm">
          <span className="font-medium truncate">{selected.name}</span>
          <button type="button" onClick={() => { onClear(); setSearch(''); }} className="ml-2 text-muted-foreground hover:text-foreground shrink-0">✕</button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/40"
        >
          <span>Select a product...</span>
          <svg className="h-4 w-4 shrink-0 opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6"/></svg>
        </button>
      )}

      {/* Dropdown panel */}
      {open && !selected && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-card shadow-xl">
          {/* Search input inside dropdown */}
          <div className="p-2 border-b border-border">
            <input
              type="text"
              autoFocus
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              placeholder="Search by name or SKU..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Results */}
          <div className="max-h-56 overflow-y-auto">
            {search.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">Type to search products</div>
            )}
            {search.length > 0 && searching && (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">Searching...</div>
            )}
            {search.length > 0 && !searching && results.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">No products found</div>
            )}
            {search.length > 0 && !searching && results.length > 0 && (
              <ul className="py-1">
                {results.slice(0, 12).map((p: { _id: string; name: string; sku?: string; sellingPrice?: number }) => (
                  <li key={p._id}>
                    <button
                      type="button"
                      onClick={() => { onSelect(p); setOpen(false); setSearch(''); }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-primary/5 flex items-center justify-between transition-colors"
                    >
                      <div>
                        <div className="font-medium">{p.name}</div>
                        {p.sku && <div className="text-xs text-muted-foreground">{p.sku}</div>}
                      </div>
                      {p.sellingPrice != null && <span className="text-xs font-mono text-muted-foreground shrink-0 ml-2">{formatINR(p.sellingPrice)}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Close hint */}
          <div className="border-t border-border px-3 py-1.5 text-xs text-muted-foreground text-center">
            <button type="button" onClick={() => setOpen(false)} className="hover:text-foreground">Close</button>
          </div>
        </div>
      )}

      {/* Backdrop to close on click outside */}
      {open && !selected && (
        <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
      )}
    </div>
  );
}

function TaxProfilesTab() {
  const { data: profiles, isLoading } = useTaxProfiles();
  const createMut = useCreateTaxProfile();
  const deleteMut = useDeleteTaxProfile();
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({ name: '', cgstRate: '0', sgstRate: '0', igstRate: '0' });

  const handleCreate = async () => { if (!form.name.trim()) return; await createMut.mutateAsync({ name: form.name, cgstRate: Number(form.cgstRate), sgstRate: Number(form.sgstRate), igstRate: Number(form.igstRate) }); setIsOpen(false); setForm({ name: '', cgstRate: '0', sgstRate: '0', igstRate: '0' }); toast.success('Tax profile created'); };
  const handleDelete = async (id: string, tName: string) => {
    if (!window.confirm(`Delete tax profile "${tName}"?`)) return;
    try { await deleteMut.mutateAsync(id); toast.success(`Tax profile "${tName}" deleted.`); }
    catch (err: any) { toast.error(err?.response?.data?.error || 'Could not delete tax profile.'); }
  };
  if (isLoading) return <Skeleton className="h-[200px]" />;

  return (
    <Card className="shadow-xs">
      <CardHeader title="Tax Profiles" action={<Button size="sm" onClick={() => setIsOpen(true)}><PlusCircle className="h-4 w-4 mr-1" /> Add</Button>} />
      <CardBody className="p-0"><div className="overflow-auto max-h-[400px]"><table className="w-full text-sm"><thead className="text-xs uppercase bg-muted/50 sticky top-0"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">CGST %</th><th className="px-4 py-3">SGST %</th><th className="px-4 py-3">IGST %</th><th className="px-4 py-3">Applicable GST</th><th className="px-4 py-3 text-right">Actions</th></tr></thead><tbody className="divide-y">{profiles?.map(t => (<tr key={t._id} className="hover:bg-muted/20"><td className="px-4 py-3 font-medium">{t.name}</td><td className="px-4 py-3">{t.cgstRate}%</td><td className="px-4 py-3">{t.sgstRate}%</td><td className="px-4 py-3">{t.igstRate}%</td><td className="px-4 py-3 font-medium"><div>Local: {t.cgstRate + t.sgstRate}%</div><div className="text-xs text-muted-foreground">Interstate: {t.igstRate}%</div></td><td className="px-4 py-3 text-right"><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDelete(t._id, t.name)}><Trash2 className="h-3.5 w-3.5" /></Button></td></tr>))}</tbody></table></div></CardBody>
      <Modal open={isOpen} onClose={() => setIsOpen(false)} title="New Tax Profile" size="sm" footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setIsOpen(false)}>Cancel</Button><Button onClick={handleCreate}>Create</Button></div>}><div className="space-y-3 py-4"><Input name="name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. GST 18%" /><div className="grid grid-cols-3 gap-2"><Input name="cgst" type="number" value={form.cgstRate} onChange={e => setForm(p => ({ ...p, cgstRate: e.target.value }))} placeholder="CGST" /><Input name="sgst" type="number" value={form.sgstRate} onChange={e => setForm(p => ({ ...p, sgstRate: e.target.value }))} placeholder="SGST" /><Input name="igst" type="number" value={form.igstRate} onChange={e => setForm(p => ({ ...p, igstRate: e.target.value }))} placeholder="IGST" /></div></div></Modal>
    </Card>
  );
}
